import { AxiosInstance, AxiosResponse } from 'axios';

export const getPrehireRedirect = async (
  axiosInstance: AxiosInstance,
  componentId?: string
): Promise<ApiResponse<string>> => {
  const response: AxiosResponse<ApiResponse<string>> = await axiosInstance.get(
    `/onboarding/prehire/redirect`,
    { params: { component_id: componentId } }
  );
  return response.data;
};

export const createPrehire = async (
  axiosInstance: AxiosInstance,
  body: NewHireRequestFormValues
): Promise<ApiResponse<PrismPrehire>> => {
  const response = await axiosInstance({
    method: 'POST',
    url: `/onboarding/prehire`,
    data: body,
  });
  return response.data;
};

export const createPrehireRequest = async (
  axiosInstance: AxiosInstance,
  id: number,
  body: NewHireRequestFormValues
): Promise<ApiResponse<PrismPrehire>> => {
  const response = await axiosInstance({
    method: 'POST',
    url: `/onboarding/prehire/new_hire_requests/${id}`,
    data: body,
    withCredentials: true, // allows cookies to be sent
  });
  return response.data;
};

export const getPrehireFields = async (
  axiosInstance: AxiosInstance
): Promise<ApiResponse<PrehireFields>> => {
  const response: AxiosResponse<ApiResponse<PrehireFields>> =
    await axiosInstance.get(`/onboarding/prehire/fields`);
  return response.data;
};

export const createNewHireRequest = async (
  axiosInstance: AxiosInstance,
  newHireRequest: NewHireRequestForm
): Promise<ApiResponse<NewHireRequestForm>> => {
  const response: AxiosResponse<ApiResponse<NewHireRequestForm>> =
    await axiosInstance.post(
      `/onboarding/prehire/new_hire_requests`,
      newHireRequest
    );
  return response.data;
};

export const getNewHireRequests = async (
  axiosInstance: AxiosInstance,
  fsmStates?: string[]
): Promise<ApiResponse<NewHireRequest[]>> => {
  const response: AxiosResponse<ApiResponse<NewHireRequest[]>> =
    await axiosInstance.get(`/onboarding/prehire/new_hire_requests`, {
      params: { fsm_states: fsmStates },
      paramsSerializer: {
        indexes: null,
      },
    });
  return response.data;
};

export const getNewHireRequestById = async (
  axiosInstance: AxiosInstance,
  id: string
): Promise<ApiResponse<NewHireRequest>> => {
  const response: AxiosResponse<ApiResponse<NewHireRequest>> =
    await axiosInstance.get(`/onboarding/prehire/new_hire_requests/${id}`);
  return response.data;
};

export const updateNewHireRequest = async (
  axiosInstance: AxiosInstance,
  id: number,
  newHireRequest: NewHireRequestForm
): Promise<ApiResponse<void>> => {
  const response: AxiosResponse<ApiResponse<void>> = await axiosInstance.put(
    `/onboarding/prehire/new_hire_requests/${id}`,
    newHireRequest
  );
  return response.data;
};

export const resendNewHireEmail = async (
  axiosInstance: AxiosInstance,
  id: number
): Promise<ApiResponse<NewHireRequest>> => {
  const response: AxiosResponse<ApiResponse<NewHireRequest>> =
    await axiosInstance.post(
      `/onboarding/prehire/new_hire_requests/${id}/resend_email`
    );
  return response.data;
};

export const deleteNewHireRequest = async (
  axiosInstance: AxiosInstance,
  id: number
): Promise<ApiResponse<NewHireRequest>> => {
  const response: AxiosResponse<ApiResponse<NewHireRequest>> =
    await axiosInstance.delete(`/onboarding/prehire/new_hire_requests/${id}`);
  return response.data;
};

export const bulkCreateQuickHireRequests = async (
  axiosInstance: AxiosInstance,
  requests: NewHireRequestForm[]
): Promise<
  ApiResponse<{
    created: Array<{
      row: number;
      email: string;
      new_hire_request_id: number;
      register_link: string;
    }>;
    errors: Array<{
      row: number;
      email: string;
      error: string;
    }>;
    total_processed: number;
    successful: number;
    failed: number;
  }>
> => {
  const response: AxiosResponse<
    ApiResponse<{
      created: Array<{
        row: number;
        email: string;
        new_hire_request_id: number;
        register_link: string;
      }>;
      errors: Array<{
        row: number;
        email: string;
        error: string;
      }>;
      total_processed: number;
      successful: number;
      failed: number;
    }>
  > = await axiosInstance.post(`/onboarding/prehire/bulk_quick_hire`, requests);
  return response.data;
};

export const bulkCompletePrehireRequests = async (
  axiosInstance: AxiosInstance,
  requests: NewHireRequestPrehireForm[]
): Promise<
  ApiResponse<{
    completed: Array<{
      row: number;
      new_hire_request_id: number;
      prism_results: any;
    }>;
    errors: Array<{
      row: number;
      new_hire_request_id: number;
      error: string;
    }>;
    total_processed: number;
    successful: number;
    failed: number;
  }>
> => {
  const response: AxiosResponse<
    ApiResponse<{
      completed: Array<{
        row: number;
        new_hire_request_id: number;
        prism_results: any;
      }>;
      errors: Array<{
        row: number;
        new_hire_request_id: number;
        error: string;
      }>;
      total_processed: number;
      successful: number;
      failed: number;
    }>
  > = await axiosInstance.post(
    `/onboarding/prehire/bulk_prehire_completion`,
    requests
  );
  return response.data;
};

export const downloadPrehireExcelTemplate = async (
  axiosInstance: AxiosInstance,
  requestIds?: number[]
): Promise<Blob> => {
  const response: AxiosResponse<Blob> = await axiosInstance.get(
    `/onboarding/prehire/new_hire_requests/template/excel`,
    {
      responseType: 'blob',
      params:
        requestIds && requestIds.length > 0
          ? { request_ids: requestIds.join(',') }
          : undefined,
    }
  );
  return response.data;
};

export const uploadPrehireExcelFile = async (
  axiosInstance: AxiosInstance,
  file: File
): Promise<ApiResponse<any[]>> => {
  const response: AxiosResponse<ApiResponse<any[]>> = await axiosInstance.post(
    `/onboarding/prehire/new_hire_requests/upload/excel`,
    file,
    {
      headers: {
        'Content-Type':
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      },
    }
  );
  return response.data;
};

export const downloadQuickHireExcelTemplate = async (
  axiosInstance: AxiosInstance
): Promise<Blob> => {
  const response: AxiosResponse<Blob> = await axiosInstance.get(
    `/onboarding/prehire/new_hire_requests/quick_hire_template/excel`,
    {
      responseType: 'blob',
    }
  );
  return response.data;
};

export const uploadQuickHireExcelFile = async (
  axiosInstance: AxiosInstance,
  file: File
): Promise<ApiResponse<any[]>> => {
  const response: AxiosResponse<ApiResponse<any[]>> = await axiosInstance.post(
    `/onboarding/prehire/new_hire_requests/upload/quick_hire_excel`,
    file,
    {
      headers: {
        'Content-Type':
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      },
    }
  );
  return response.data;
};

export const getNewHireQuestions = async (
  axiosInstance: AxiosInstance,
  locationCode: string
): Promise<ApiResponse<NewHireQuestionsData>> => {
  const response: AxiosResponse<ApiResponse<NewHireQuestionsData>> =
    await axiosInstance.get(`/onboarding/prehire/new_hire_questions`, {
      params: { location_code: locationCode },
    });
  return response.data;
};

export const getTerminatedEmployees = async (
  axiosInstance: AxiosInstance
): Promise<ApiResponse<TerminatedEmployee[]>> => {
  const response: AxiosResponse<ApiResponse<TerminatedEmployee[]>> =
    await axiosInstance.get(`/onboarding/rehire/employees`);
  return response.data;
};

export const getTerminatedEmployee = async (
  axiosInstance: AxiosInstance,
  employeeId: string
): Promise<ApiResponse<TerminatedEmployeeDetail>> => {
  const response: AxiosResponse<ApiResponse<TerminatedEmployeeDetail>> =
    await axiosInstance.get(`/onboarding/rehire/employees/${employeeId}`);
  return response.data;
};

export const rehireEmployee = async (
  axiosInstance: AxiosInstance,
  body: RehireFormValues
): Promise<ApiResponse<unknown>> => {
  const response: AxiosResponse<ApiResponse<unknown>> =
    await axiosInstance.post(`/onboarding/rehire`, body);
  return response.data;
};

export const onboardingService = {
  createPrehire,
  createPrehireRequest,
  deleteNewHireRequest,
  getPrehireRedirect,
  getPrehireFields,
  getNewHireQuestions,
  getNewHireRequests,
  getNewHireRequestById,
  createNewHireRequest,
  updateNewHireRequest,
  resendNewHireEmail,
  bulkCreateQuickHireRequests,
  bulkCompletePrehireRequests,
  downloadPrehireExcelTemplate,
  uploadPrehireExcelFile,
  downloadQuickHireExcelTemplate,
  uploadQuickHireExcelFile,
  getTerminatedEmployees,
  getTerminatedEmployee,
  rehireEmployee,
};
